import {AutogenLayoutCashAdvanceSlideout} from "./autogen/AutogenLayoutCashAdvanceSlideout";
import {DropdownItem, Layout, SaveButton, SlideoutDecorator, Snackbar, Toast} from "@mcleod/components";
import {Api, Currency, CurrencyUtil, LogManager} from "@mcleod/core";

const log = LogManager.getLogger("lme.dispatch.asset.CashAdvanceSlideout");

export interface CashAdvanceLayoutProps {
    driverFullName: string;
    driverId: string;
    cardNum: string;
    vendorInterfaceId: string;
    vendorDesc: string;
    accountId: string;
    subAccountId: string;
    payeeId: string;
    tractorId: string;
    assigneeType: string;
    cardStatus: string;
}

interface EFSCashAdvanceLimits {
    policyCardCash: Currency;
    oneTimeCash: Currency;
    totalCash: Currency;
    contractLimit: Currency;
    cashAvail: Currency;
    userOneTimeCashLimit: Currency;
}

interface ComdataCashAdvanceLimits {
    cash: Currency;
    cashUsed: Currency;
    oneTimeLimit: Currency;
    totalAvailable: Currency;
    userOneTimeCashLimit: Currency;
    totalCash: Currency;
}

interface ServiceResponse {
    isSuccess: boolean;
    resultStatus: string;
    responseMessage: string;
}

export class CashAdvanceSlideout extends AutogenLayoutCashAdvanceSlideout {

    private _efsCashAdvanceLimits: EFSCashAdvanceLimits;
    private _comdataCashAdvanceLimits: ComdataCashAdvanceLimits;
    private static _isSlideoutActive = false;

    get efsCashAdvanceLimits(): EFSCashAdvanceLimits {
        return this._efsCashAdvanceLimits;
    }

    get comdataCashAdvanceLimits(): ComdataCashAdvanceLimits {
        return this._comdataCashAdvanceLimits;
    }

    static get isSlideoutActive(): boolean {
        return this._isSlideoutActive;
    }

    static set isSlideoutActive(value: boolean) {
        this._isSlideoutActive = value;
    }

    public static async showSlideout(props: CashAdvanceLayoutProps) {
        const self = Layout.getLayout("lme/dispatch/asset/CashAdvanceSlideout") as CashAdvanceSlideout;
        const saveButton = SaveButton.create({
            id: "cashAdvanceLimitSaveButton",
            caption: "Send One-Time Cash Limit",
            tooltip: "Update the one-time cash limit for this fuel card",
            backgroundColor: "primary",
            color: "white",
            fontBold: true,
            width: 200,
            minWidth: 170,
            minHeight: 35,
            borderWidth: 0,
            paddingLeft: 10,
            paddingRight: 10,
            rowBreak: false,
            enabled: false,
            onClick: async () => {
                // Disable the textboxes while processing
                saveButton.busy = true;
                self.textboxAmount.enabled = false;
                self.textboxNotes.enabled = false;
                try {
                    if (self.validateRequiredFields(self)) {
                        await this.replaceOneTimeCashLimit(self, props);
                    } else {
                        Snackbar.showWarningSnackbar("Required fields are missing values.");
                    }
                } finally {
                    // Re-enable the textboxes after processing, and conditionally re-enable the save button
                    saveButton.busy = false;
                    saveButton.enabled = self.validateRequiredFields(self) && this.validateAmountLimit(self, props);
                    self.textboxAmount.enabled = true;
                    self.textboxNotes.enabled = true;
                }
            }
        });

        new SlideoutDecorator({
            layout: self,
            fillHeight: true,
            width: "50%",
            fillVerticalSpace: true,
            backgroundColor: "defaultBackground",
            title: `${props.driverFullName} - ${self.truncateToLast4(props.cardNum)} - ${props.vendorDesc}`,
            addlComponents: saveButton,

            doAfterSlideIn: async () => {
                const progressBar: Snackbar = Snackbar.showDownloadSnackbar("Loading data", "Loading vendor data...", {
                    persist: true,
                    targetPanel: self,
                });
                this.isSlideoutActive = true;
                saveButton.enabled = false;

                self.displayVendorSpecificFields(props.vendorInterfaceId);

                self.textboxDriverName._captionLabel.color = "default.lightest";
                self.textboxCardNum._captionLabel.color = "default.lightest";
                self.textboxVendor._captionLabel.color = "default.lightest";

                self.textboxCardNum.captionProps = {
                    paddingLeft: 0
                };
                self.textboxVendor.captionProps = {
                    paddingLeft: 0
                };

                self.textboxCardNum.text = self.truncateToLast4(props.cardNum);
                self.textboxVendor.text = props.vendorDesc;
                self.textboxDriverName.text = props.driverFullName;

                const updateSaveButtonState = () => {
                    saveButton.enabled = self.validateRequiredFields(self) &&
                        this.validateAmountLimit(self, props);
                };

                try {
                    const limits = await self.getLimits(props);

                    if (self.isEfsVendor(props.vendorInterfaceId)) {
                        const efsLimits = limits as EFSCashAdvanceLimits;
                        self.textboxPolicyCardCash.text = CurrencyUtil.formatCurrency(efsLimits.policyCardCash);
                        self.textboxOneTimeCash.text = CurrencyUtil.formatCurrency(efsLimits.oneTimeCash);
                        self.textboxTotalCash.text = CurrencyUtil.formatCurrency(efsLimits.totalCash);
                        self.textboxContractLimit.text = CurrencyUtil.formatCurrency(efsLimits.contractLimit);
                        self.textboxAmountAvailable.text = CurrencyUtil.formatCurrency(efsLimits.cashAvail);

                    } else {
                        const comdataLimits = limits as ComdataCashAdvanceLimits;
                        self.textboxCash.text = CurrencyUtil.formatCurrency(comdataLimits.cash);
                        self.textboxOneTimeLimit.text = CurrencyUtil.formatCurrency(comdataLimits.oneTimeLimit);
                        self.textboxTotalAvailable.text = CurrencyUtil.formatCurrency(comdataLimits.totalAvailable);
                        self.textboxCashUsed.text = CurrencyUtil.formatCurrency(comdataLimits.cashUsed);
                        self.textboxTotalCash.text = CurrencyUtil.formatCurrency(comdataLimits.totalCash);
                    }
                } catch (error) {
                    log.error(error);
                    progressBar.dismiss();
                    throw error;
                }

                self.textboxAmount.addChangeListener(() => {
                    updateSaveButtonState();
                });

                this.setupHistoryTableDateRangeSelection(self);

                await self.layoutCashAdvanceHistory.mainDataSource.search({
                    fuel_interface_id: props.vendorInterfaceId,
                    fuel_card_id: props.cardNum,
                    earliest_date: self.textboxHistoryDateRange.selectedItem.value.split(",")[0],
                    latest_date: self.textboxHistoryDateRange.selectedItem.value.split(",")[1],
                });

                updateSaveButtonState();
                progressBar.dismiss();
            },
            doAfterSlideOut: async () => {
                this.isSlideoutActive = false;
            }
        });
    }

    private static validateAmountLimit(self: CashAdvanceSlideout, props: CashAdvanceLayoutProps): boolean {
        if (!self.textboxAmount.text) return false;

        const limit = self.isEfsVendor(props.vendorInterfaceId)
            ? self.efsCashAdvanceLimits.userOneTimeCashLimit
            : self.comdataCashAdvanceLimits.userOneTimeCashLimit;

        return self.validateLoadAmt(self.textboxAmount.text, limit);
    }

    private validateRequiredFields(self: CashAdvanceSlideout): boolean {
        return self.textboxNotes.validateSimple() &&
            self.textboxAmount.validateSimple();
    }

    private displayVendorSpecificFields(vendorId: string) {
        if (this.isEfsVendor(vendorId)) {
            this.textboxCash.visible = false;
            this.textboxCashUsed.visible = false;
            this.textboxOneTimeLimit.visible = false;
            this.textboxTotalAvailable.visible = false;

            this.textboxPolicyCardCash.visible = true;
            this.textboxOneTimeCash.visible = true;
            this.textboxTotalCash.visible = true;
            this.textboxContractLimit.visible = true;
            this.textboxAmountAvailable.visible = true;

        } else {
            this.textboxCash.visible = true;
            this.textboxCashUsed.visible = true;
            this.textboxOneTimeLimit.visible = true;
            this.textboxTotalAvailable.visible = true;
            this.textboxTotalCash.visible = true;

            this.textboxPolicyCardCash.visible = false;
            this.textboxOneTimeCash.visible = false;
            this.textboxContractLimit.visible = false;
            this.textboxAmountAvailable.visible = false;
        }
    }

    private static setupHistoryTableDateRangeSelection(self: CashAdvanceSlideout) {
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
        const today = new Date(Date.now()).toISOString().split("T")[0];

        self.textboxHistoryDateRange.items = [
            new DropdownItem(`${sevenDaysAgo} 00:00:00,${today} 23:59:59`, "Last 7 Days"),
        ];
        self.textboxHistoryDateRange.selectedItem = self.textboxHistoryDateRange.items[0];
    }

    private static async replaceOneTimeCashLimit(self: CashAdvanceSlideout, props: CashAdvanceLayoutProps) {
        const serviceResponse = await self.send(props);

        if (serviceResponse.isSuccess === true) {
            await self.slideOut();
            Toast.showSuccessToast("Processing complete", `${serviceResponse.responseMessage}`);
        } else {
            Snackbar.showWarningSnackbar(`${serviceResponse.responseMessage}`);
        }
    }

    private validateLoadAmt(loadAmt: string, userOneTimeCashLimit: Currency): boolean {
        if (!loadAmt) return false;

        loadAmt = loadAmt.replace(/[^0-9.]/g, "");
        if (!loadAmt) return false;

        const loadAmtCurrency: Currency = new Currency(Number(loadAmt));
        const userOneTimeCashLimitCurrency: Currency = new Currency(userOneTimeCashLimit);

        if (CashAdvanceSlideout.isSlideoutActive && loadAmtCurrency.isGreaterThan(new Currency(userOneTimeCashLimitCurrency))) {
            this.textboxAmount.showAdhocTooltip(
                `The load amount of ${loadAmtCurrency} exceeds the maximum of ${userOneTimeCashLimitCurrency}`,
                { shaking: true, timeout: 5000 });
            this.textboxAmount.focus();
            return false;
        }
        return true;
    }

    public async send(props: CashAdvanceLayoutProps): Promise<ServiceResponse> {

        const loadAmt = new Currency(Number(this.textboxAmount.text.replace(/[^0-9.]/g, "")));

        const result = await Api.post("lme/dispatch/asset/replace-one-time-limit", {
            card_num: props.cardNum,
            vendor_id: props.vendorInterfaceId,
            account_id: props.accountId,
            sub_account_id: props.subAccountId,
            load_amt: loadAmt,
            note: this.textboxNotes.text,
            driver_id: props.driverId,
            driver_name: props.driverFullName,
            payee_id: props.payeeId,
            tractor_id: props.tractorId,
            assignee_type: props.assigneeType,
            card_status: props.cardStatus,
        });

        return {
            isSuccess: result.data[0].is_success,
            resultStatus: result.data[0].result_status,
            responseMessage: result.data[0].response_message,
        };
    }

    public async getLimits(layoutProps: CashAdvanceLayoutProps): Promise<EFSCashAdvanceLimits | ComdataCashAdvanceLimits> {
        const response = await Api.search("lme/dispatch/asset/fuel-card-limits", {
            vendor_id: layoutProps.vendorInterfaceId,
            card_num: layoutProps.cardNum,
            account_id: layoutProps.accountId,
            sub_account_id: layoutProps.subAccountId,
        });

        const responseLimits = response.data[0];

        if (this.isEfsVendor(layoutProps.vendorInterfaceId)) {
            this._efsCashAdvanceLimits = {
                policyCardCash: responseLimits?.policy_card_cash,
                oneTimeCash: responseLimits?.one_time_cash_limit,
                totalCash: responseLimits?.total_cash,
                contractLimit: responseLimits?.contract_limit,
                cashAvail: responseLimits?.cash_avail,
                userOneTimeCashLimit: responseLimits?.user_one_time_limit,
            };
            return this._efsCashAdvanceLimits;

        } else {
            this._comdataCashAdvanceLimits = {
                cash: responseLimits.cash,
                cashUsed: responseLimits.cash_used,
                oneTimeLimit: responseLimits.one_time_limit,
                totalAvailable: responseLimits.total_available,
                userOneTimeCashLimit: responseLimits.user_one_time_limit,
                totalCash: responseLimits.total_cash,
            }
            return this._comdataCashAdvanceLimits;
        }
    }

    private truncateToLast4(fullString: string): string {
        return fullString.replace(/.(?=.{4})/g, "");
    }

    private isEfsVendor(vendorId: string): boolean {
        return "H" === vendorId;
    }
}
